import { type ReactNode } from 'react'

import { ChromeComposerTextarea } from './ChromeComposerTextarea'
import { chromeToggleChipClass } from './ChromeSurface'
import { HorizontalChipStrip } from './HorizontalChipStrip'

export type SettingsHierarchyOption = {
  value: string
  label: string
  description?: ReactNode
  title?: string
}

export function SettingsGroup({
  angular,
  title,
  description,
  children,
}: {
  angular: boolean
  title: string
  description?: ReactNode
  children: ReactNode
}) {
  return (
    <section
      className={[
        'border border-line bg-[color-mix(in_srgb,var(--panel)_88%,transparent)] px-3 py-3 shadow-sm sm:px-4 sm:py-4',
        angular ? 'rounded-none border-2' : 'rounded-[var(--radius)]',
      ].join(' ')}
    >
      <div className="border-b border-line/70 pb-3">
        <p className="text-sm font-semibold text-ink">{title}</p>
        {description ? <p className="mt-1 text-xs leading-relaxed text-ink-soft">{description}</p> : null}
      </div>
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  )
}

export function SettingsOptionGroup({
  angular,
  title,
  description,
  ariaLabel,
  options,
  value,
  onChange,
  customValue = 'custom',
  customLabel,
  customText,
  customPlaceholder,
  customMaxLength,
  onCustomTextChange,
}: {
  angular: boolean
  title: string
  description?: ReactNode
  ariaLabel: string
  options: SettingsHierarchyOption[]
  value: string
  onChange: (value: string) => void
  customValue?: string
  customLabel?: string
  customText?: string
  customPlaceholder?: string
  customMaxLength?: number
  onCustomTextChange?: (value: string) => void
}) {
  const selected = options.find((opt) => opt.value === value)
  const customActive = value === customValue && onCustomTextChange != null

  return (
    <section className="space-y-2">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-ink">{title}</p>
        {description ? <p className="mt-0.5 text-xs leading-relaxed text-ink-soft">{description}</p> : null}
      </div>

      <div
        className={[
          'overflow-hidden border border-line bg-[color-mix(in_srgb,var(--surface-muted)_55%,var(--panel))]',
          angular ? 'rounded-none border-2' : 'rounded-[calc(var(--radius)-2px)]',
        ].join(' ')}
      >
        <HorizontalChipStrip
          className="border-b border-line/70 bg-panel/40 px-2 py-2"
          trackClassName="gap-2"
          role="radiogroup"
          aria-label={ariaLabel}
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              title={typeof opt.description === 'string' ? opt.description : opt.title}
              onClick={() => onChange(opt.value)}
              className={[chromeToggleChipClass(angular, value === opt.value), 'w-auto flex-none'].join(' ')}
            >
              {opt.label}
            </button>
          ))}
        </HorizontalChipStrip>

        <div
          className={[
            'border border-t-0 bg-[color-mix(in_srgb,var(--accent)_9%,var(--surface-muted))] px-3 py-2.5',
            'border-[color-mix(in_srgb,var(--accent)_55%,var(--line))]',
            angular ? 'rounded-none' : 'rounded-b-[calc(var(--radius)-2px)]',
          ].join(' ')}
        >
          {customActive ? (
            <label className="block text-sm font-semibold text-ink">
              {customLabel ?? '自定义说明'}
              <ChromeComposerTextarea
                angular={angular}
                variant="form"
                value={customText ?? ''}
                onChange={(e) => onCustomTextChange(e.target.value)}
                placeholder={customPlaceholder}
                maxLength={customMaxLength}
                className="!mt-0"
              />
            </label>
          ) : (
            <div>
              <p className="text-xs font-semibold text-ink">{selected?.label ?? '当前选择'}</p>
              <p className="mt-1 text-xs leading-relaxed text-ink-soft">
                {selected?.description ?? '该选项会作为当前配置写入后续生成描述'}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
